import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function ResellerDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* ── Topbar ── */}
      <header className="h-14 bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition flex-shrink-0"
            aria-label="Toggle navigation"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="hidden sm:inline text-white font-semibold text-[15px]">Reseller Portal</span>
          <span className="bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px]">
            Reseller
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button aria-label="Notifications" className="relative w-[34px] h-[34px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>

          <div className="flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-pb flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden sm:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
        </div>
      </header>

      {/* ── Below topbar ── */}
      <div className="flex flex-1 relative">

        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        <aside className={`
          flex flex-shrink-0 flex-col pt-4 pb-6 overflow-y-auto
          w-[220px] bg-white border-r border-[#E5E7EB]
          fixed top-14 bottom-0 left-0 z-40
          transition-transform duration-200 ease-in-out
          md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <nav className="px-2 space-y-0.5" onClick={() => setSidebarOpen(false)}>
            <div className="flex items-center gap-2 px-[10px] py-2 rounded-[8px] text-[13px] bg-blue-light text-pb font-semibold">
              <span className="flex-1">Network Overview</span>
            </div>
          </nav>

          <div className="border-t border-[#E5E7EB] mx-[10px] my-[6px]" />

          <div className="px-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-[10px] py-2 rounded-[8px] text-[13px] text-dr hover:bg-red-light transition-colors"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 px-3 py-4 md:px-6 md:py-8 overflow-y-auto min-w-0">
          <h2 className="text-[20px] font-bold text-[#111827] mb-2">Network Overview</h2>
          <p className="text-[#6B7280] text-[13px] mb-8">Manage your accounts and view tours across your network.</p>

          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-10 text-center">
            <div className="w-12 h-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#374151] mb-1">Coming soon</p>
            <p className="text-[13px] text-[#9CA3AF] max-w-xs mx-auto">
              The reseller network dashboard is under development. Contact MST to discuss early access.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
